import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Capacitor } from '@capacitor/core';
import { Geolocation as NativeGeolocation } from '@capacitor/geolocation';
import { Haptics } from '@capacitor/haptics';
import { jwtDecode } from 'jwt-decode';
import { DeviceDetectorService } from 'ngx-device-detector';
import { BehaviorSubject, Observable, timeout } from 'rxjs';
import { v4 as uuidv4 } from 'uuid';
import {
  getDistanceUsingHaversine,
  isLargeScreen,
} from '../../../global.variable';
import { ModalDialogueService } from '../components/modal-dialogue/modal-dialogue.service';
import { SplashScreenComponent } from '../components/splash-screen/splash-screen.component';
import { LoadingSpinnerService } from './loading-spinner.service';
import { ToasterService } from './toaster.service';

@Injectable({
  providedIn: 'root',
})
export class DeviceInfoService {
  deviceInfoSubject = new BehaviorSubject({});

  appStatus: boolean = true;
  // param to reverse the pages status
  locationDisabled: boolean = false;

  isSuperUser: boolean = false;

  lastShownTime: any;

  loggedInDeviceID = this.getUniqueDeviceID();
  splashScreenOpened = false;

  largeView: Boolean = isLargeScreen;

  constructor(
    private deviceService: DeviceDetectorService,
    private http: HttpClient,
    private spinnerService: LoadingSpinnerService,
    private toasterService: ToasterService,
    private dialogService: ModalDialogueService,
  ) {
    try {
      const userData = JSON.parse(localStorage.getItem('userData') || '{}');
      const userToken = userData?.user_token;

      if (userToken) {
        const decodedToken: any = jwtDecode(userToken);
        this.isSuperUser = Boolean(decodedToken?.is_superuser);
      }
    } catch (error) {}

    this.onLineOfflineCheck();

    window.addEventListener('online', () => {
      // adding a delay so that the device gets connected properly before calling the server
      setTimeout(() => {
        this.onLineOfflineCheck();
      }, 3500);
    });
    window.addEventListener('offline', () => {
      this.onLineOfflineCheck();
    });
    this.deviceInfoSubject.subscribe((value: any) => {
      if ('appStatus' in value) {
        this.appStatus = value?.appStatus;
      }
    });
  }
  isPhoneIOS() {
    return /iPad|iPhone|iPod/.test(navigator.userAgent);
  }
  isDevicePWA() {
    return Boolean(window.matchMedia('(display-mode: standalone)').matches);
  }
  async isDeviceChrome() {
    let deviceInfo: any = await this.getDeviceInfo();
    return deviceInfo?.browser?.toLowerCase() === 'chrome';
  }
  onLineOfflineCheck() {
    this.deviceInfoSubject.next({
      appStatus: navigator.onLine && this.appStatus,
    });
  }
  isRegistrationComplete() {
    // method to check if the registartion is complete. T
    // he user may not have given access to location so the spinnerhide will interput the subscription apis
    const firstTimeSubscriber = localStorage.getItem('firstTimeSubscriber');
    return firstTimeSubscriber !== 'true';
  }
  async checkGPSPermissionState() {
    try {
      const permission = await navigator.permissions.query({
        name: 'geolocation' as PermissionName,
      });

      return permission.state;
    } catch (error) {
      return false;
    }
  }

  async getApiHeadersAndExtras(token: string) {
    return new Promise((resolve) => {
      if (!this.loggedInDeviceID) {
        this.loggedInDeviceID = this.getUniqueDeviceID();
      }
      const isSuperUserAccess =
        localStorage.getItem(btoa('superUserAccess')) === 'true';
      const headerDict: any = {
        Authorization: `Bearer ${token}`,
        timeoffset: String(new Date().getTimezoneOffset()),
        tzInfo: Intl.DateTimeFormat().resolvedOptions().timeZone,
        loggedInDeviceID: this.loggedInDeviceID,
      };

      try {
        if (isSuperUserAccess) {
          headerDict['isSuperUserAccess'] = '1';
        } else if (!this.largeView) {
          // we need this only for mobile users, pcs mostly will be stationary
          localStorage.setItem(
            btoa('lastApiCalledTime'),
            String(new Date().getTime()),
          );
          const diffSeconds: any = 30; // 30 seconds
          const JsonGpsRecord = JSON.parse(
            localStorage.getItem('gpsRecord') || '{}',
          );
          const lastSavedGpsRecord = JSON.parse(
            localStorage.getItem('lastSavedGpsRecord') || '{}',
          );
          // passing gps in all the api calls to track user. if the date time is same as the previous due to simultaneous api call, discard that. and also when the gps has not changed
          if (
            JsonGpsRecord?.recorded_at &&
            (!lastSavedGpsRecord?.recorded_at ||
              (lastSavedGpsRecord?.recorded_at &&
                JsonGpsRecord?.recorded_at >=
                  lastSavedGpsRecord?.recorded_at + diffSeconds &&
                (lastSavedGpsRecord?.gps?.lat !== JsonGpsRecord?.gps?.lat ||
                  lastSavedGpsRecord?.gps?.lon !== JsonGpsRecord?.gps?.lon)))
          ) {
            localStorage.setItem(
              'lastSavedGpsRecord',
              JSON.stringify(JsonGpsRecord),
            );
            headerDict['gpsRecord'] = localStorage.getItem('gpsRecord');
          }
        }
      } catch (error) {
        throw error;
      } finally {
        if (this.isRegistrationComplete()) this.updateGpsCache();

        resolve({
          headerDict: headerDict,
          reqBody: {
            delayed_event: this.appStatus && navigator.onLine,
          },
        });
      }
    });
  }

  async setIp() {
    try {
      this.getAwaitIP();
    } catch (error) {
      // Handle the error (e.g., log it or show a message)
      throw error;
    }
  }
  async handleIpApiError() {
    return await new Promise(async (resolve, reject) => {
      if (window.localStorage.getItem('ipAddress')) {
        resolve(window.localStorage.getItem('ipAddress'));
      }
      try {
        const TIMEOUT_IN_MS = 5000;
        this.backupApiCall()
          .pipe(
            timeout(TIMEOUT_IN_MS), // This will raise an error if no response is received within the timeout value
          )
          .subscribe({
            next: (resp: any) => {
              if (resp?.ipAddress) {
                window.localStorage.setItem('ipAddress', resp.ipAddress);
                resolve(resp.ipAddress);
              }
            },
            error: (err) => {
              if (err.name === 'TimeoutError') {
                resolve('null');
              } else {
                resolve('null');
              }
            },
          });
      } catch (error) {
        resolve('null');
        throw error;
      }
    });
  }
  backupApiCall(): Observable<any> {
    return this.http.get('https://freeipapi.com/api/json');
  }
  getIP(): Observable<any> {
    return this.http.get('api/shared/get_client_ip/');
  }
  async getAwaitIP() {
    const TIMEOUT_IN_MS = 5000;
    return await new Promise(async (resolve, reject) => {
      try {
        this.getIP()
          .pipe(
            timeout(TIMEOUT_IN_MS), // This will raise an error if no response is received within the timeout value
          )
          .subscribe({
            next: (resp: any) => {
              if (resp?.ip) {
                if (resp?.lat && resp?.lon && this.largeView) {
                  localStorage.setItem(
                    btoa('ipGeoLocationForDesktop'),
                    JSON.stringify({ lat: resp?.lat, lon: resp?.lon }),
                  );
                }
                window.localStorage.setItem('ipAddress', resp.ip);
                resolve(resp.ip);
              }
            },
            error: async (err) => {
              if (err.name === 'TimeoutError') {
                resolve(await this.handleIpApiError());
              } else {
                // Handle other errors
              }
            },
          });
        setTimeout(() => {
          if (!window.localStorage.getItem('ipAddress')) {
            this.handleIpApiError();
          }
        }, 5000);
      } catch (error) {
        resolve(await this.handleIpApiError());
        throw error;
      }
    });
  }

  async watchPositionWithLowAccuracy(regardTimeOut = false) {
    if (Capacitor.isNativePlatform()) {
      return await new Promise(async (resolve) => {
        let nativeWatchId: string | null = null;
        try {
          const permissions = await NativeGeolocation.requestPermissions();
          if (permissions.location !== 'granted') {
            // the chances of this occurring is very low as if the
            // permission is denied it will be blocked in the high accuracy watch postion method itself and wont reach here.
            // Still we will stop the low accuracy watch postion and update the status
            if (this.isRegistrationComplete()) this.spinnerService.hide();
            this.deviceInfoSubject.next({
              permissionStatus: 'locationDisabled',
            });
            this.locationDisabled = true;
            resolve({});
            return;
          }

          nativeWatchId = await NativeGeolocation.watchPosition(
            { timeout: 1500, enableHighAccuracy: false, maximumAge: 5000 },
            async (position, error) => {
              if (position && !error) {
                this.stopGPSTracking(nativeWatchId);
                resolve({
                  lat: position.coords.latitude,
                  lon: position.coords.longitude,
                  source: 0,
                });
                return;
              }
              if (error) {
                this.stopGPSTracking(nativeWatchId);
                // These include the POSITION_UNAVAILABLE error, which is hard to produce.
                // So will use the same strategy as that of the other error possible ie TIMEOUT.
                // The below logic is mainly intended for the TIMEOUT scenario which can occur for multiple reasons
                // 1. Offline and doesn't have a clear sky view https://github.com/Baseflow/flutter-geolocator/issues/1312
                // 2. User gave browser permission but turned off gps at phone level
                // we will stop this watch position and stop wasting time and
                // check if the localStorage value is stale and pass it if its not other wise update the user that gps is not available
                const JsonGpsRecord = JSON.parse(
                  localStorage.getItem('gpsRecord') || '{}',
                );

                // check if the record is stale ie > 10 minutes. if so pass that value. T
                // his is done mainly for the Offline and doesn't have a clear sky view https://github.com/Baseflow/flutter-geolocator/issues/1312

                // also check regardTimeOut = true => we dont need to take the value from localStorage in that case.
                // This will be true for the gps polling method ( updateGpsCache() )
                if (regardTimeOut) return;
                const largeView = isLargeScreen;
                const checkCondition =
                  JsonGpsRecord?.gps?.lat &&
                  JsonGpsRecord?.recorded_at &&
                  (new Date().getTime() - JsonGpsRecord?.recorded_at * 1000) /
                    60000 <
                    (largeView ? 60 : 10);

                if (checkCondition) {
                  resolve({
                    lat: JsonGpsRecord?.gps?.lat,
                    lon: JsonGpsRecord?.gps?.lon,
                    source: 3,
                    timeStamp: JsonGpsRecord?.recorded_at,
                    orginalSource: JsonGpsRecord?.gps?.source,
                  });
                } else {
                  // Else we will just tell the user gps is not available
                  this.spinnerService.hide();
                  this.deviceInfoSubject.next({
                    permissionStatus: 'locationDisabled',
                  });
                  if (this.isSuperUser)
                    this.toasterService.setMessage({
                      errorMessage: 'Timeout while getting your location',
                      successMessage: '',
                    });
                  this.locationDisabled = true;
                }
              }
            },
          );
        } catch (err) {
          this.stopGPSTracking(nativeWatchId);
          resolve({});
          return;
        }
      });
    }
    return await new Promise((resolve, reject) => {
      let lowAccuracyWatchId = navigator.geolocation.watchPosition(
        (position) => {
          // if we are getting a postion we can stop the watchPostion then and there.
          navigator.geolocation.clearWatch(lowAccuracyWatchId);

          resolve({
            lat: position.coords.latitude,
            lon: position.coords.longitude,
            source: 1,
          });
        },
        async (error) => {
          if (error.code === error.PERMISSION_DENIED) {
            // the chances of this occurring is very low as if the
            // permission is denied it will be blocked in the high accuracy watch postion method itself and wont reach here.
            // Still we will stop the low accuracy watch postion and update the status
            navigator.geolocation.clearWatch(lowAccuracyWatchId);
            if (this.isRegistrationComplete()) this.spinnerService.hide();
            this.deviceInfoSubject.next({
              permissionStatus: 'locationDisabled',
            });

            // this.toasterService.setMessage({
            //   errorMessage: 'This action requires Location Services permission',
            //   successMessage: '',
            // });
            this.locationDisabled = true;
          } else {
            navigator.geolocation.clearWatch(lowAccuracyWatchId);
            // These include the POSITION_UNAVAILABLE error, which is hard to produce.
            // So will use the same strategy as that of the other error possible ie TIMEOUT.
            // The below logic is mainly intended for the TIMEOUT scenario which can occur for multiple reasons
            // 1. Offline and doesn't have a clear sky view https://github.com/Baseflow/flutter-geolocator/issues/1312
            // 2. User gave browser permission but turned off gps at phone level
            // we will stop this watch position and stop wasting time and
            // check if the localStorage value is stale and pass it if its not other wise update the user that gps is not available
            const JsonGpsRecord = JSON.parse(
              localStorage.getItem('gpsRecord') || '{}',
            );

            // check if the record is stale ie > 10 minutes. if so pass that value. T
            // his is done mainly for the Offline and doesn't have a clear sky view https://github.com/Baseflow/flutter-geolocator/issues/1312

            // also check regardTimeOut = true => we dont need to take the value from localStorage in that case.
            // This will be true for the gps polling method ( updateGpsCache() )
            if (regardTimeOut) return;
            const checkCondition =
              JsonGpsRecord?.gps?.lat &&
              JsonGpsRecord?.recorded_at &&
              (new Date().getTime() - JsonGpsRecord?.recorded_at * 1000) /
                60000 <
                (this.largeView ? 60 : 10);

            if (checkCondition) {
              resolve({
                lat: JsonGpsRecord?.gps?.lat,
                lon: JsonGpsRecord?.gps?.lon,
                source: 3,
                timeStamp: JsonGpsRecord?.recorded_at,
                orginalSource: JsonGpsRecord?.gps?.source,
              });
            } else {
              // Else we will just tell the user gps is not available
              this.spinnerService.hide();
              this.deviceInfoSubject.next({
                permissionStatus: 'locationDisabled',
              });
              if (this.isSuperUser)
                this.toasterService.setMessage({
                  errorMessage: 'Timeout while getting your location',
                  successMessage: '',
                });
              this.locationDisabled = true;
            }
          }
        },
        {
          timeout: 1500,
          enableHighAccuracy: false,
          maximumAge: 5000,
        },
      );
    });
  }
  openLocationSplashScreen() {
    if (!this.splashScreenOpened) {
      this.splashScreenOpened = true;
      const dialogRef = this.dialogService.open(SplashScreenComponent, {
        data: {},
      });
      dialogRef
        .afterClosed()
        .subscribe(() => (this.splashScreenOpened = false));
    }
  }

  async watchPositionWithHighAccuracy(
    regardTimeOut = false,
    gpsOptional = false,
    count = 0,
  ) {
    if (Capacitor.isNativePlatform()) {
      // native app
      return await new Promise(async (resolve) => {
        let nativeWatchId: string | null = null;

        try {
          const permissions = await NativeGeolocation.requestPermissions();

          if (permissions.location !== 'granted') {
            if (this.isRegistrationComplete()) this.spinnerService.hide();
            this.deviceInfoSubject.next({
              permissionStatus: 'locationDisabled',
            });
            this.openLocationSplashScreen();
            this.locationDisabled = true;
            resolve({});
            return;
          }

          nativeWatchId = await NativeGeolocation.watchPosition(
            { timeout: 2000, enableHighAccuracy: true, maximumAge: 5000 },
            async (position, error) => {
              if (position && !error) {
                this.stopGPSTracking(nativeWatchId);
                resolve({
                  lat: position.coords.latitude,
                  lon: position.coords.longitude,
                  source: 0,
                });
                return;
              }
              if (error) {
                this.stopGPSTracking(nativeWatchId);
                if (gpsOptional) {
                  resolve({});
                } else {
                  count += 1;
                  if (count < 2) {
                    resolve(
                      await this.watchPositionWithHighAccuracy(
                        regardTimeOut,
                        gpsOptional,
                        count,
                      ),
                    );
                  } else {
                    resolve(
                      await this.watchPositionWithLowAccuracy(regardTimeOut),
                    );
                  }
                }
              }
            },
          );
        } catch (err) {
          this.openLocationSplashScreen();
          this.stopGPSTracking(nativeWatchId);

          resolve({});
          return;
        }
      });
    }

    // web apps
    return await new Promise((resolve) => {
      let highAccuracyWatchId = navigator.geolocation.watchPosition(
        (position) => {
          navigator.geolocation.clearWatch(highAccuracyWatchId);
          resolve({
            lat: position.coords.latitude,
            lon: position.coords.longitude,
            source: 0,
          });
        },
        async (error) => {
          if (gpsOptional) {
            resolve({});
          } else if (error.code === error.PERMISSION_DENIED) {
            navigator.geolocation.clearWatch(highAccuracyWatchId);
            if (this.isRegistrationComplete()) this.spinnerService.hide();
            this.deviceInfoSubject.next({
              permissionStatus: 'locationDisabled',
            });
            this.locationDisabled = true;
            resolve({});
          } else {
            count += 1;
            if (count < 2) {
              navigator.geolocation.clearWatch(highAccuracyWatchId);
              resolve(
                await this.watchPositionWithHighAccuracy(
                  regardTimeOut,
                  gpsOptional,
                  count,
                ),
              );
            } else {
              navigator.geolocation.clearWatch(highAccuracyWatchId);
              resolve(await this.watchPositionWithLowAccuracy(regardTimeOut));
            }
          }
        },
        { timeout: 2000, enableHighAccuracy: true, maximumAge: 5000 },
      );
    });
  }
  sleep(ms = 1000) {
    return new Promise((resolve) => setTimeout(resolve, ms));
  }
  async stopGPSTracking(nativeWatchId: any) {
    if (nativeWatchId) {
      // Copy the current ID and immediately nullify the reference to avoid double-clearing
      const idToClear = nativeWatchId;
      nativeWatchId = null;

      try {
        await NativeGeolocation.clearWatch({ id: idToClear });
      } catch (e) {}
    }
  }

  async getGpsForPagesComponent() {
    if (Capacitor.isNativePlatform()) {
      // native app
      return await new Promise(async (resolve, reject) => {
        let nativeWatchId: string | null = null;
        try {
          const permissions = await NativeGeolocation.requestPermissions();
          if (permissions.location !== 'granted') {
            reject({ message: 'user-denied-permission' });
            return;
          }

          nativeWatchId = await NativeGeolocation.watchPosition(
            { timeout: 2500, enableHighAccuracy: true, maximumAge: 5000 },
            async (position, error) => {
              if (position && !error) {
                if (nativeWatchId)
                  await NativeGeolocation.clearWatch({ id: nativeWatchId });
                resolve({
                  lat: position.coords.latitude,
                  lon: position.coords.longitude,
                  source: 0,
                });
                return;
              }
              if (error) {
                if (nativeWatchId)
                  await NativeGeolocation.clearWatch({ id: nativeWatchId });
                reject(error);

                return;
              }
            },
          );
        } catch (err) {
          if (nativeWatchId)
            await NativeGeolocation.clearWatch({ id: nativeWatchId });
          reject(err);

          return;
        }
      });
    }
    return await new Promise((resolve, reject) => {
      let highAccuracyWatchId = navigator.geolocation.watchPosition(
        (position) => {
          navigator.geolocation.clearWatch(highAccuracyWatchId);

          resolve({
            lat: position.coords.latitude,
            lon: position.coords.longitude,
            source: 0,
          });
          return;
        },
        (error) => {
          reject(error);
          navigator.geolocation.clearWatch(highAccuracyWatchId);
          return;
        },
        {
          timeout: 2500,
          enableHighAccuracy: true,
          maximumAge: 5000,
        },
      );
    });
  }

  async getIp() {
    return new Promise(async (resolve) => {
      resolve(
        window.localStorage.getItem('ipAddress')
          ? window.localStorage.getItem('ipAddress')
          : await this.getAwaitIP(),
      );
    });
  }

  async setAndGetDeviceInfo() {
    return await new Promise(async (resolve) => {
      let deviceInfo = this.deviceService.getDeviceInfo();
      window.localStorage.setItem('deviceInfo', JSON.stringify(deviceInfo));

      resolve(JSON.stringify(deviceInfo));
    });
  }
  async getDeviceInfo() {
    return new Promise(async (resolve) => {
      try {
        const deviceInfo: any = window.localStorage.getItem('deviceInfo')
          ? window.localStorage.getItem('deviceInfo')
          : await this.setAndGetDeviceInfo();

        resolve(JSON.parse(deviceInfo));
      } catch (err) {
        resolve(this.deviceService.getDeviceInfo());
      }
    });
  }
  getGpsCoordinates = async (
    regardTimeOut: true | false = false,
    gpsOptional: true | false = false,
  ) => {
    const isIOSFirstTimeUser =
      (this.isPhoneIOS() &&
        !this.largeView &&
        !('gtAppTheme' in localStorage)) ||
      localStorage.getItem(btoa('isIOSFirstTimeUser')) === 'true';

    if (isIOSFirstTimeUser) return;

    if (this.largeView) {
      // if the device is large screen get the value from the cache and no need to call it real time. only call it if its very old
      const JsonGpsRecord = JSON.parse(
        localStorage.getItem('gpsRecord') || '{}',
      );

      const checkCondition =
        JsonGpsRecord?.gps?.lat &&
        JsonGpsRecord?.recorded_at &&
        (new Date().getTime() - JsonGpsRecord?.recorded_at * 1000) / 60000 <
          24 * 60;

      if (checkCondition) {
        return {
          lat: JsonGpsRecord?.gps?.lat,
          lon: JsonGpsRecord?.gps?.lon,
          source: JsonGpsRecord?.gps?.source == 0 ? 3 : 2,
          timeStamp: JsonGpsRecord?.recorded_at,
          orginalSource: JsonGpsRecord?.gps?.source,
          largeView: 1,
        };
      }
      let deskTopLocation = JSON.parse(
        localStorage.getItem(btoa('ipGeoLocationForDesktop')) || '{}',
      );

      if (deskTopLocation) {
        return {
          lat: deskTopLocation?.lat,
          lon: deskTopLocation?.lon,
          source: 2,
          largeView: 1,
        };
      }
      return { source: 4, largeView: 1 };
    }
    return await this.watchPositionWithHighAccuracy(regardTimeOut, gpsOptional);
  };
  updateGpsCache() {
    // https://app.shortcut.com/sendem/story/13184/review-and-update-location-permissions
    // no need to track desktop users as they will be stationary
    if (this.largeView) return;
    const isIOSFirstTimeUser =
      (this.isPhoneIOS() &&
        !this.largeView &&
        !('gtAppTheme' in localStorage)) ||
      localStorage.getItem(btoa('isIOSFirstTimeUser')) === 'true';

    if (isIOSFirstTimeUser) return;

    this.watchPositionWithHighAccuracy(true).then((gps: any) => {
      let gpsRecord = { gps: gps, recorded_at: new Date().getTime() / 1000 };
      if (gps?.lat && gps?.lon) {
        // this.checkGeoFenceAlertData(gps);
        localStorage.setItem('gpsRecord', JSON.stringify(gpsRecord));
        if (this.locationDisabled) {
          this.locationDisabled = false;
          this.deviceInfoSubject.next({
            permissionStatus: 'locationEnabled',
          });
        }
      }
    });
  }
  async getDeviceDetail(gpsCondition: 'must' | 'optional' = 'must', gps?: any) {
    try {
      const isSuperUserAccess =
        localStorage.getItem(btoa('superUserAccess')) === 'true';

      if (isSuperUserAccess) {
        return { isSuperUserAccess: true };
      }

      let finalGps = gps;

      if (!(finalGps?.lat && finalGps?.lon)) {
        finalGps = await this.getGpsCoordinates(
          false,
          gpsCondition === 'optional',
        );
      }

      if (finalGps?.lat && finalGps?.lon && finalGps?.source !== 3) {
        const gpsRecord = {
          gps: finalGps,
          recorded_at: Date.now() / 1000,
        };
        localStorage.setItem('gpsRecord', JSON.stringify(gpsRecord));
      }

      const [deviceInfo, ip]: any = await Promise.all([
        this.getDeviceInfo(),
        this.getIp(),
      ]);

      return {
        gps: {
          ...finalGps,
          ip,
        },
        ...deviceInfo,
      };
    } catch (error) {
      throw error;
    }
  }

  checkGeoFenceAlertData(userLocation: any) {
    const geoFenceAlertData = JSON.parse(
      localStorage.getItem(btoa('geoFenceAlertData')) || '[]',
    );

    function fetchHaversineDistance(alertData: any) {
      const haversineDistance: any = getDistanceUsingHaversine(
        userLocation?.lat,
        alertData?.gps?.lat,
        userLocation?.lon,
        alertData?.gps?.lon,
      );
      return haversineDistance * 1000;
    }

    const now = new Date().getTime();
    const alertData = geoFenceAlertData
      ?.map((alertData: any) => {
        const haversineDistance = fetchHaversineDistance(alertData);
        return { ...alertData, haversineDistance: haversineDistance };
      })
      .filter(
        (alertData: any) =>
          alertData?.startDateTime <= now &&
          now <= alertData?.endDateTime &&
          alertData?.haversineDistance > Number(alertData?.geoFenceDistance),
      );
    if (!alertData?.length) {
      // removing this value because if the user goes inside, we will restart the check.
      sessionStorage.removeItem('geoFenceLastAlertTime');
    }
    this.deviceInfoSubject.next({
      geoFenceAlert: alertData,
    });
  }
  getUniqueDeviceID(): string {
    // we will store a unique ID to the Localstorage and pass it to backend mainly for websocket communication to avoid data refresh from the device from the which the change was orginated
    let uniqueDeviceID = localStorage.getItem(btoa('uniqueDeviceID'));
    if (!uniqueDeviceID) {
      uniqueDeviceID = `${uuidv4()}-${new Date().getTime()}`;
      localStorage.setItem(btoa('uniqueDeviceID'), uniqueDeviceID);
    }
    return uniqueDeviceID;
  }
  delay = (ms: any) => new Promise((resolve) => setTimeout(resolve, ms));
  triggerCustomPulsePattern = async (patternArray = [200]) => {
    // Web Browsers fully support pattern arrays natively
    if (!Capacitor.isNativePlatform()) {
      if (
        typeof window !== 'undefined' &&
        window.navigator &&
        window.navigator.vibrate
      ) {
        try {
          window.navigator.vibrate(patternArray);
        } catch (error) {}
      }
      return;
    }

    try {
      for (let i = 0; i < patternArray.length; i++) {
        if (i % 2 === 0) {
          // Even indexes are vibration steps
          await Haptics.vibrate({ duration: patternArray[i] });
          // Wait for the vibration to finish
          await this.delay(patternArray[i]);
        } else {
          // Odd indexes are pause steps
          await this.delay(patternArray[i]);
        }
      }
    } catch (error) {}
  };
}
